<template>
  <div class="customer-list-container">
    <!-- Toast Notifications -->
    <CToaster :autohide="3000" class="toast-container">
      <CToast
        v-for="info in infoList"
        :key="info.id"
        :show="true"
        :header="info.header"
        :color="info.color"
      >
        {{ info.message }}.
      </CToast>
    </CToaster>

    <!-- Professional Customer List -->
    <CRow>
      <CCol sm="12">
        <CCard class="customer-list-card">
          <CCardHeader class="list-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div class="header-content">
                <i class="fas fa-users me-2 text-primary"></i>
                <strong>Customer List</strong>
                <span 
                  class="info-tooltip ms-2"
                  v-c-tooltip.hover="{
                    content: 'This table shows all customers. Use column filters to search. Click on a row to view more details.'
                  }"
                >
                  <i class="fas fa-info-circle text-muted"></i>
                </span>
              </div>
              <CBadge color="secondary" class="results-badge">
                {{ computedItems.length }} Results
              </CBadge>
            </div>
          </CCardHeader>
          <CCardBody class="list-body p-0">
            <!-- Mobile Card View (visible on small screens) -->
            <div class="mobile-card-view d-md-none">
              <div v-if="loading" class="loading-state">
                <div class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading customer data...</p>
                </div>
              </div>
              
              <div v-else-if="computedItems.length === 0" class="empty-state">
                <div class="text-center py-5">
                  <i class="fas fa-users fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No customers found</h5>
                  <p class="text-muted">Try adjusting your search criteria or add a new customer.</p>
                  <CButton
                    v-if="canAdd"
                    color="primary"
                    variant="outline"
                    @click="addNew"
                  >
                    <i class="fas fa-plus me-2"></i>
                    Add New Customer
                  </CButton>
                </div>
              </div>

              <div v-else class="mobile-cards">
                <div 
                  v-for="item in computedItems" 
                  :key="item.id"
                  class="mobile-card"
                  @click="toggleDetails(item)"
                >
                  <div class="mobile-card-header">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="customer-info">
                        <h6 class="mb-1">{{ item.name }}</h6>
                        <small class="text-muted">IC: {{ item.icNumber }}</small>
                      </div>
                      <div class="mobile-actions">
                        <CBadge color="primary" class="mobile-badge">
                          Customer
                        </CBadge>
                      </div>
                    </div>
                  </div>
                  
                  <div class="mobile-card-body">
                    <div class="row g-2">
                      <div class="col-6">
                        <small class="text-muted d-block">Phone</small>
                        <span>{{ item.phone || 'N/A' }}</span>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Email</small>
                        <span>{{ item.email || 'N/A' }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="mobile-card-footer">
                    <CButton
                      :color="canUpdate ? 'primary' : 'info'"
                      size="sm"
                      @click.stop="onEdit(item)"
                    >
                      <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                      {{ canUpdate ? 'Edit' : 'View' }}
                    </CButton>
                    <small class="text-muted ms-auto">
                      Tap to {{ item._toggled ? 'collapse' : 'expand' }}
                    </small>
                  </div>

                  <!-- Mobile Details Expansion -->
                  <CCollapse :show="Boolean(item._toggled)">
                    <div class="mobile-details">
                      <hr class="my-3">
                      <div class="mobile-details-content">
                        <div class="detail-info">
                          <p class="text-muted mb-2">Name: {{ item.name }}</p>
                          <p class="text-muted mb-2">IC Number: {{ item.icNumber }}</p>
                          <p class="text-muted mb-2">Phone: {{ item.phone || 'N/A' }}</p>
                          <p class="text-muted mb-3">Email: {{ item.email || 'N/A' }}</p>
                        </div>
                        
                        <div class="mobile-actions">
                          <CButton
                            size="sm"
                            :color="canUpdate ? 'primary' : 'info'"
                            class="me-2 mb-2"
                            @click.stop="onEdit(item)"
                          >
                            <i :class="canUpdate ? 'fas fa-edit me-1' : 'fas fa-eye me-1'"></i>
                            {{ canUpdate ? 'Edit' : 'View' }}
                          </CButton>
                          <CButton
                            v-if="canDelete"
                            size="sm"
                            color="danger"
                            class="mb-2"
                            @click.stop="showDeleteConfirmation(item)"
                          >
                            <i class="fas fa-trash me-1"></i>
                            Delete
                          </CButton>
                        </div>
                      </div>
                    </div>
                  </CCollapse>
                </div>
              </div>
            </div>

            <!-- Desktop Table View (hidden on small screens) -->
            <div class="desktop-table-view d-none d-md-block">
              <div class="table-responsive">
                <CDataTable
                  :items="computedItems"
                  :fields="professionalFields"
                  :column-filter="{ external: true, lazy: true }"
                  :column-filter-value.sync="columnFilterValue"
                  :sorter="{ external: true, resetable: true }"
                  :sorter-value.sync="sorterValue"
                  :items-per-page="itemsPerPage"
                  :active-page="1"
                  hover
                  :loading="loading"
                  @row-clicked="handleRowClick"
                  class="professional-table"
                >
                  <!-- Index Column -->
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">{{ index + 1 }}</span>
                    </td>
                  </template>

                  <!-- Name Column -->
                  <template #name="{ item }">
                    <td>
                      <div>
                        <strong>{{ item.name }}</strong>
                        <!-- <br>
                        <small class="text-muted">Customer ID: {{ item.id }}</small> -->
                      </div>
                    </td>
                  </template>

                  <!-- IC Number Column -->
                  <template #icNumber="{ item }">
                    <td>
                      <span>{{ item.icNumber }}</span>
                    </td>
                  </template>

                  <!-- Phone Column -->
                  <template #phone="{ item }">
                    <td>
                      <span>{{ item.phone || 'N/A' }}</span>
                    </td>
                  </template>

                  <!-- Email Column -->
                  <template #email="{ item }">
                    <td>
                      <span>{{ item.email || 'N/A' }}</span>
                    </td>
                  </template>

                  <!-- Details Expansion -->
                  <template #details="{ item }">
                    <CCollapse
                      :show="Boolean(item._toggled)"
                      :duration="collapseDuration"
                    >
                      <div class="details-wrapper">
                        <CCard class="details-card">
                          <CCardHeader>
                            <div class="d-flex justify-content-between align-items-center">
                              <h6 class="mb-0">
                                <i class="fas fa-info-circle me-2"></i>
                                Customer Details - {{ item.name }}
                              </h6>
                              <CBadge color="primary">
                                Customer
                              </CBadge>
                            </div>
                          </CCardHeader>
                          <CCardBody>
                            <!-- Customer Information -->
                            <CRow>
                              <CCol md="6">
                                <div class="info-section">
                                  <h6>
                                    <i class="fas fa-user me-2"></i>Personal Information
                                  </h6>
                                  <p><strong>Full Name:</strong> {{ item.name }}</p>
                                  <p><strong>IC Number:</strong> {{ item.icNumber }}</p>
                                </div>
                              </CCol>
                              <CCol md="6">
                                <div class="info-section">
                                  <h6>
                                    <i class="fas fa-address-card me-2"></i>Contact Information
                                  </h6>
                                  <p><strong>Phone:</strong> {{ item.phone || 'Not provided' }}</p>
                                  <p><strong>Email:</strong> {{ item.email || 'Not provided' }}</p>
                                </div>
                              </CCol>
                            </CRow>

                            <!-- Action Buttons -->
                            <div class="mt-3 pt-3 border-top">
                              <CButton
                                :color="canUpdate ? 'primary' : 'info'"
                                class="me-2"
                                @click="onEdit(item)"
                              >
                                <i :class="canUpdate ? 'fas fa-edit me-2' : 'fas fa-eye me-2'"></i>
                                {{ canUpdate ? 'Edit Customer' : 'View Customer' }}
                              </CButton>
                              <CButton
                                v-if="canDelete"
                                color="danger"
                                variant="outline"
                                @click="showDeleteConfirmation(item)"
                              >
                                <i class="fas fa-trash me-2"></i>
                                Delete Customer
                              </CButton>
                            </div>
                          </CCardBody>
                        </CCard>
                      </div>
                    </CCollapse>
                  </template>
                </CDataTable>
              </div>

              <!-- Pagination -->
              <div class="pagination-wrapper" v-show="pages > 1">
                <CPagination
                  :pages="pages"
                  :active-page.sync="activePage"
                  @update:active-page="refreshTable"
                />
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="loading-state">
                <div class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading customer data...</p>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="!loading && computedItems.length === 0" class="empty-state">
                <div class="text-center py-5">
                  <i class="fas fa-users fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No customers found</h5>
                  <p class="text-muted">Try adjusting your search criteria or add a new customer.</p>
                  <CButton
                    v-if="canAdd"
                    color="primary"
                    variant="outline"
                    @click="addNew"
                  >
                    <i class="fas fa-plus me-2"></i>
                    Add New Customer
                  </CButton>
                </div>
              </div>
            </div>
          </CCardBody>
          
          <!-- Enhanced Footer -->
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <CButton
                  v-if="canAdd"
                  color="primary"
                  @click="addNew"
                  class="me-2"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New Customer
                </CButton>
              </div>
              
              <div class="footer-info">
                <small class="text-muted">
                  Showing {{ computedItems.length }} of {{ pages * itemsPerPage }} customers
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>

        <!-- Delete Confirmation Modal -->
        <CModal
          title="Confirm Delete"
          color="warning"
          :show.sync="warningModal"
        >
          <template #header>
            <h5 class="modal-title">
              <i class="fas fa-exclamation-triangle me-2"></i>
              Confirm Delete
            </h5>
          </template>

          <div class="modal-body">
            <p>Are you sure you want to delete customer <strong>{{ itemToDelete.name }}</strong>?</p>
            <p class="text-muted small">This action cannot be undone.</p>
          </div>

          <template #footer>
            <CButton
              color="secondary"
              @click="warningModal = false"
            >
              Cancel
            </CButton>
            <CButton
              color="danger"
              @click="onDeleteConfirmation"
            >
              <i class="fas fa-trash me-2"></i>
              Delete
            </CButton>
          </template>
        </CModal>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import CustomerApi from "../../lib/customerApi";

export default {
  name: "CustomerList",
  data() {
    return {
      // Data arrays
      loadedItems: [],
      
      // Filter states
      columnFilterValue: {},
      tableFilterValue: "",
      sorterValue: { column: null, asc: true },
      
      // Pagination
      activePage: 1,
      itemsPerPage: 20,
      pages: 1,
      
      // UI states
      loading: true,
      collapseDuration: 0,
      
      // Professional standardized fields
      professionalFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        { 
          key: "name", 
          label: "Name", 
          _style: "min-width:200px",
          sorter: true 
        },
        { 
          key: "icNumber", 
          label: "IC Number",
          _style: "width:150px",
          sorter: true 
        },
        { 
          key: "phone", 
          label: "Phone",
          _style: "width:130px",
          sorter: true 
        },
        { 
          key: "email", 
          label: "Email",
          _style: "min-width:200px",
          sorter: true 
        },
      ],
      
      // API and notifications
      api: new CustomerApi(),
      infoList: [],
      
      // Modal states
      warningModal: false,
      itemToDelete: {},
    };
  },

  mounted() {
    // Check view permission
    if (!this.canView) {
      this.$router.push({ name: 'Page403' });
      return;
    }
    this.refreshTable();
  },

  watch: {
    reloadParams() {
      this.refreshTable();
    },
  },

  computed: {
    reloadParams() {
      return [
        this.sorterValue,
        this.columnFilterValue,
        this.tableFilterValue,
        this.activePage,
        this.itemsPerPage,
      ];
    },

    computedItems() {
      return this.loadedItems.map((item) => {
        return {
          ...item,
          // Add any computed fields for display here
          displayName: item.name || 'Unknown',
          displayIC: item.icNumber || 'N/A',
          displayPhone: item.phone || 'N/A',
          displayEmail: item.email || 'N/A',
        };
      });
    },
    canView() {
      return this.$auth.hasModulePermission('CUSTOMER', 'CanView');
    },
    canAdd() {
      return this.$auth.hasModulePermission('CUSTOMER', 'CanAdd');
    },
    canDelete() {
      return this.$auth.hasModulePermission('CUSTOMER', 'CanDelete');
    },
    canUpdate() {
      return this.$auth.hasModulePermission('CUSTOMER', 'CanUpdate');
    },
  },

  methods: {
    // Navigation methods
    addNew() {
      this.$router.push({ path: "/admins/Customer" });
    },
    
    onEdit(item) {
      this.$router.push({
        path: `/admins/Customer/${item.id}`,
      });
    },

    // Table interaction methods
    handleRowClick(item) {
      this.toggleDetails(item);
    },

    toggleDetails(item) {
      this.$set(item, "_toggled", !item._toggled);
      this.collapseDuration = 300;
      this.$nextTick(() => {
        this.collapseDuration = 0;
      });
    },

    // Data loading method - standardized name
    refreshTable() {
      this.loading = true;
      
      // Backend now supports 'name' field directly - no mapping needed
      const mappedFilters = { ...this.columnFilterValue };
      
      if (mappedFilters.name) {
        console.log("Sending name filter to backend as 'name':", mappedFilters.name);
      }
      
      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        sorter: this.sorterValue,
        filters: mappedFilters,
        search: this.tableFilterValue,
      };

      console.log("Fetching customer data with params:", params);

      this.api
        .getList(params)
        .then((response) => {
          if (response.result) {
            this.loadedItems = [...response.result.items];
            this.pages = Math.ceil(response.result.totalItems / this.itemsPerPage);
            console.log("Loaded customers:", this.loadedItems.length);
            console.log("Total pages:", this.pages);
            
            // Debug: Check if filtering is working
            const filterValue = mappedFilters.name;
            if (filterValue) {
              const matchingNames = this.loadedItems.filter(item => 
                item.name && item.name.toLowerCase().includes(filterValue.toLowerCase())
              );
              console.log(`Filter "${filterValue}": ${matchingNames.length}/${this.loadedItems.length} customers match (should match backend results)`);
            }
          } else {
            console.error("Unexpected response format:", response);
            this.loadedItems = [];
            this.pages = 1;
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error("Error loading customer data:", error);
          this.toast("Error", "Failed to load customer data", "danger");
          this.loading = false;
        });
    },

    // Notification method
    toast(header, message, color) {
      const toastId = `toast-${Date.now()}-${Math.random()}`;
      this.infoList.push({
        id: toastId,
        header: header,
        message: message,
        color: color,
      });

      // Auto-remove toast after autohide duration (3000ms + small buffer)
      setTimeout(() => {
        const index = this.infoList.findIndex(info => info.id === toastId);
        if (index !== -1) {
          this.infoList.splice(index, 1);
        }
      }, 3500);
    },

    // Delete methods
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },

    onDeleteConfirmation() {
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.toast("Success", "Customer deleted successfully", "success");
          this.refreshTable();
          this.warningModal = false;
          this.itemToDelete = {};
        })
        .catch(() => {
          this.toast("Error", "Failed to delete customer", "danger");
          this.warningModal = false;
          this.itemToDelete = {};
        });
    },
  },
};
</script>

<style scoped>
/* Professional Container - Using CoreUI's default background */
.customer-list-container {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Professional Customer List Card */
.customer-list-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.list-header {
  border-bottom: 1px solid var(--cui-border-color, #d8dbe0);
}

.header-content {
  display: flex;
  align-items: center;
}

.results-badge {
  font-size: 0.8125rem;
  font-weight: 500;
}

.info-tooltip {
  cursor: help;
}

/* Mobile Card View */
.mobile-card-view {
  padding: 1rem;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: var(--cui-card-bg, #fff);
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.mobile-card-header {
  margin-bottom: 0.75rem;
}

.customer-info h6 {
  color: var(--cui-primary, #321fdb);
  font-weight: 600;
}

.mobile-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.mobile-card-body {
  margin-bottom: 0.75rem;
}

.mobile-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid var(--cui-border-color-translucent, rgba(0, 0, 0, 0.125));
}

.mobile-details {
  background: var(--cui-body-bg, #f8f9fa);
  margin: 0 -1rem -1rem -1rem;
  padding: 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

.mobile-details-content {
  background: var(--cui-card-bg, #fff);
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Desktop Table View */
.desktop-table-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr:hover {
  cursor: pointer;
}

/* Details Section */
.details-wrapper {
  padding: 1rem;
  background: var(--cui-body-bg, #f8f9fa);
}

.details-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.info-section {
  background: var(--cui-body-bg, #f8f9fa);
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Pagination */
.pagination-wrapper {
  padding: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Footer */
.card-footer {
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

.footer-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.footer-info {
  display: flex;
  align-items: center;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3.75rem 1.25rem;
}

/* Mobile Responsiveness */
@media (max-width: 767.98px) {
  .customer-list-container {
    padding: 1rem;
  }
  
  .list-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.75rem;
  }
  
  .footer-actions {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .footer-actions .btn {
    width: 100%;
  }
}

@media (max-width: 575.98px) {
  .customer-list-container {
    padding: 0.75rem;
  }
  
  .list-header {
    padding: 1rem;
  }
  
  .mobile-card {
    padding: 0.75rem;
  }
}

/* Print Styles */
@media print {
  .customer-list-container {
    padding: 0;
  }
  
  .mobile-card-view {
    display: none;
  }
  
  .desktop-table-view {
    display: block !important;
  }
  
  .professional-table {
    font-size: 0.75rem;
  }
  
  .card-footer,
  .pagination-wrapper {
    display: none;
  }
}
</style>